import React, { useState, useEffect } from "react";
import { Form, Input, Button, Card, message, Typography } from "antd";
import { useHistory } from "react-router-dom";
import * as com from "../Common";
import { getLOOnePagerAccess } from "../LOOnePager/loOnePagerClient";
import { track } from "statsig";
import { STATSIG, PAGE } from "statsig/events";

export default function EncompassSetup() {
  const [encompassLOID, setEncompassLOID] = useState("");
  const [losUrl, setLosUrl] = useState("");
  // A customer with no Encompass instance keys the link on the loan officer's
  // own id and has no Encompass user id to enter; the server says which.
  const [loanOfficerId, setLoanOfficerId] = useState("");
  const [encompassEnabled, setEncompassEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // The loan officer's own application page is open to some companies only
  // (web/go/src/intake/lo_onepager.go); the server says whether this one is.
  const [loPageAllowed, setLoPageAllowed] = useState(false);
  const history = useHistory();
  const token = sessionStorage.getItem("ZeitroA");

  useEffect(() => {
    if (!token) return;
    getLOOnePagerAccess()
      .then((a) => setLoPageAllowed(!!a))
      .catch(() => setLoPageAllowed(false));
  }, [token]);

  useEffect(() => {
    const page = window.location.pathname.includes("/app/gmcccalculators")
      ? PAGE.ONE_PAGE_APP_SETUP
      : PAGE.SETTINGS_ONE_PAGE_APP_SETUP
    track(STATSIG.COMMON.VIEW, page, { page })
    if (!token) return;
    setLoading(true);
    fetch("/los/gmcc/encompassloid", {
      method: "GET",
      headers: { Authorization: "Bearer " + token, Cache: "no-cache" },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === "OK") {
          setEncompassLOID(data.encompass_loid || "");
          setLosUrl(data.los_url || "");
          setLoanOfficerId(data.loan_officer_id || "");
          if (typeof data.encompass_enabled === "boolean") setEncompassEnabled(data.encompass_enabled);
        }
      })
      .catch(() => message.error("Failed to load Encompass LOID"))
      .finally(() => setLoading(false));
  }, [token]);

  const onFinish = (values) => {
    if (!token) return;
    setSaving(true);
    fetch("/los/gmcc/encompassloid", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify({ encompass_loid: values.encompass_loid || "" }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === "OK") {
          message.success("Encompass LOID saved. One page app loans will be assigned to you in Encompass.");
          const encompassUserID = values.encompass_loid || ""
          setEncompassLOID(encompassUserID)
          track(STATSIG.ONE_PAGE_APP_SETUP.UPDATE, encompassUserID, { encompass_userid: encompassUserID })
        } else message.error("Failed to save");
      })
      .catch(() => message.error("Failed to save Encompass LOID"))
      .finally(() => setSaving(false));
  };

  const customerId = com.getCustomerId();
  // Host comes from the customer's LOS URL (global.customers.los); fall back to
  // the current origin if it isn't configured.
  const host = (losUrl || window.location.origin).replace(/\/+$/, "");
  const quickIntakeLink = customerId && encompassLOID
    ? `${host}/${customerId}/${encompassLOID}/onepage`
    : "";
  // Without Encompass the link carries the loan officer's own id instead.
  const plainLink = customerId && loanOfficerId ? `${host}/${customerId}/${loanOfficerId}/onepage` : "";
  // Same link, ending in /lo-onepage: the form filed by the loan officer, with
  // the prefilled 1003 reviewed before it goes to Encompass.
  const loLinkPath = customerId && encompassLOID ? `/${customerId}/${encompassLOID}/lo-onepage` : "";
  const loLink = loLinkPath ? `${host}${loLinkPath}` : "";

  return (
    <div style={{ width: "100%", height: "100%",padding:"24px 32px" }} header={null}>
   
    <h2 style={{ fontSize: 24, fontWeight: 500, marginBottom: 16 }}>One page app setup</h2>

      {!encompassEnabled ? (
        <p style={{ marginBottom: 20, color: "#666" }}>
          Share this link with your borrowers. They fill in one page — contact details, loan, property,
          declarations and supporting documents — and the application lands in your pipeline with the
          1003 prefilled from the documents. They do not need an account to submit it.
        </p>
      ) : (
        <p style={{ marginBottom: 20, color: "#666" }}>
          Set your Encompass user ID (LOID) so that loans created from your one page app link are assigned to you in Encompass.
        </p>
      )}
      {loading ? (
        <p>Loading...</p>
      ) : !encompassEnabled ? (
        plainLink ? (
          <div>
            <Typography.Text strong>Your one page app link:</Typography.Text>
            <Typography.Paragraph copyable={{ text: plainLink, onCopy: () => track(STATSIG.ONE_PAGE_APP_SETUP.COPY, plainLink, { url: plainLink }) }} style={{ marginTop: 8, marginBottom: 0 }}>
              <Typography.Text style={{ wordBreak: "break-all" }}>{plainLink}</Typography.Text>
            </Typography.Paragraph>
          </div>
        ) : (
          <Typography.Text type="danger">Your one page app link is not available. Please contact support.</Typography.Text>
        )
      ) : (
        <>
          <Form key={"encompass-" + encompassLOID} layout="vertical" onFinish={onFinish} initialValues={{ encompass_loid: encompassLOID }}>
            <Form.Item name="encompass_loid" label={<span style={{ fontWeight: 500 ,fontSize:16}}>Encompass userID</span>}>
              <Input size='large' placeholder="Your Encompass user ID" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" size='large' htmlType="submit" loading={saving}>
                Save
              </Button>
            </Form.Item>
          </Form>
          {encompassLOID ? (
            <div style={{ marginTop: 24, paddingTop: 24, borderTop: "1px solid #f0f0f0" }}>
              <Typography.Text strong>Configured Encompass userID:</Typography.Text>
              <div style={{ marginTop: 4, marginBottom: 20 }}>
                <Typography.Text code>{encompassLOID}</Typography.Text>
              </div>
              {quickIntakeLink ? (
                <>
                  <Typography.Text strong>Your one page app link:</Typography.Text>
                  <Typography.Paragraph copyable={{ text: quickIntakeLink, onCopy: () => track(STATSIG.ONE_PAGE_APP_SETUP.COPY, quickIntakeLink, { url: quickIntakeLink }) }} style={{ marginTop: 8, marginBottom: 0 }}>
                    <Typography.Text style={{ wordBreak: "break-all" }}>{quickIntakeLink}</Typography.Text>
                  </Typography.Paragraph>
                </>
              ) : null}
              {loPageAllowed && loLink ? (
                <div style={{ marginTop: 20 }}>
                  <Typography.Text strong>Your loan officer application link:</Typography.Text>
                  <div style={{ color: "#666", fontSize: 13, margin: "4px 0 8px" }}>
                    Enter a borrower's basics and documents yourself, review the prefilled 1003, then sync it to Encompass.
                  </div>
                  <Typography.Paragraph copyable={{ text: loLink }} style={{ marginBottom: 8 }}>
                    <Typography.Text style={{ wordBreak: "break-all" }}>{loLink}</Typography.Text>
                  </Typography.Paragraph>
                  <Button type="primary" onClick={() => history.push(loLinkPath)}>
                    Start an application
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
